import "./style.scss";

import { CircularProgress } from "@mui/material";
import { Box } from "@mui/system";
import MessageBlock from "components/shared/MessageBlock";
import StyledButton from "components/shared/StyledButton";
import { StyledDatePicker } from "components/shared/StyledDatePicker";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import ReinstateProvisionField from "components/shared/ReinstateProvisionField/ReinstateProvisionField";
import React, { useEffect, useMemo, useState } from "react";
import { SmallBody } from "helpers/TypographyComponents";
import { useTranslation } from "react-i18next";
import type { GeneralTabTerm } from "store/managementStore";
import {
    addGlobalProvision,
    fetchGlobalProvisionImpactedTerms,
    termCanReinstate as termCanReinstateFromStore,
} from "store/periodStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { addProvisions, fetchPreviousInitialProvision } from "store/termStore";
import variables from "../../../../assets/styles/_variables.module.scss";
import { formatNumber } from "helpers/utils";
import StatusChip, { IStatusRule } from "components/shared/StatusChip";

export const labelStyles = {
    color: "#737373",
    width: "100%",
    justifyContent: "space-between",
    fontSize: "12px",
    lineHeight: "18px",
    ".MuiTypography-root": { fontSize: "12px" },
    "&.MuiInputLabel-root": { fontFamily: "Poppins", fontStyle: "normal", width: "200px" },
};

type TProps = {
    close: () => void;
    typeModal: string | null;
    selectedRows: (number | undefined)[];
    terms: GeneralTabTerm[];
};

/**
 * All values from the DB are stored *100 (cents for amounts, *100 for percentages).
 * e.g. 50% is stored as 5000, 1.50€ is stored as 150.
 * formatNumber expects values in cents so we divide by 100 only at display time.
 */
const toCurrency = (cents: number) => formatNumber(cents / 100, { isCurrency: true, always2Digit: false });

const toLocalDateString = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/**
 * Base physical provision = currentProvision - previousInitialProvision * reinstatedProvisionPercent / 10000
 */
const computeBasePhysicalProvision = (
    currentProvision: number,
    previousInitialProvision: number,
    reinstatedProvisionPercent: number,
) => currentProvision - (previousInitialProvision * reinstatedProvisionPercent) / 10000;

/**
 * New physical provision = base * newPct / provisionPercent
 * newPct is the raw user input (e.g. "50" for 50%).
 * provisionPercent is stored as percent * 100 in DB.
 */
const computeNewPhysicalProvision = (base: number, newPct: number, provisionPercent: number, grossPhysical: number) =>
    provisionPercent > 0 ? (base * newPct) / (provisionPercent / 100) : (grossPhysical * newPct) / 100;

/**
 * Current reinstatement amount = previousInitialProvision * reinstatedProvisionPercent / 10000
 * reinstatedProvisionPercent is stored as percent * 100 in DB.
 */
const computeCurrentReinstatement = (previousInitialProvision: number, reinstatedProvisionPercent: number) =>
    (previousInitialProvision * reinstatedProvisionPercent) / 10000;

/**
 * New reinstatement amount = previousInitialProvision * newPct / 100
 * newPct is the raw user input (e.g. "50" for 50%).
 */
const computeNewReinstatement = (previousInitialProvision: number, newPct: number) =>
    (previousInitialProvision * newPct) / 100;

/**
 * A term's reinstatement can be modified if:
 * - initialProvision > 0 (provision is partially retained), OR
 * - initialProvision === 0 AND reinstatedProvisionPercent === 10000 (fully reinstated at 100%)
 */
const termCanReinstate = (term: GeneralTabTerm) =>
    (term.initialProvision ?? 0) > 0 || term.reinstatedProvisionPercent === 10000;

/** Stored DB percent (*100) → input string, only when all selected terms share the same value > 0 */
const sharedPercentInput = (terms: GeneralTabTerm[], getStored: (term: GeneralTabTerm) => number) => {
    if (!terms.length) return "";
    const first = getStored(terms[0]);
    if (first <= 0 || terms.some((term) => getStored(term) !== first)) return "";
    return String(first / 100);
};

const AddProvisions = ({ close, typeModal, selectedRows, terms }: TProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { activePeriod, addGlobalProvisionStatus, globalProvisionImpactedTerms, globalProvisionImpactedTermsStatus } =
        useAppSelector((state) => state.period);
    const { addProvisionsStatus } = useAppSelector((state) => state.term);

    const selectedTerms = useMemo(() => terms.filter((term) => selectedRows.includes(term.id)), [terms, selectedRows]);
    const selectedTermIds = useMemo(() => selectedTerms.map((term) => term.id), [selectedTerms]);

    const [date, setDate] = React.useState<Date | null | string | dateFns>(null);
    const [percentage, setPercentage] = React.useState(() =>
        sharedPercentInput(selectedTerms, (term) => term.provisionPercent ?? 0),
    );
    const [reinstatePercentage, setReinstatePercentage] = React.useState(() =>
        sharedPercentInput(selectedTerms, (term) => term.reinstatedProvisionPercent ?? 0),
    );
    const [previousInitialProvision, setPreviousInitialProvision] = useState<number | null>(null);

    const isSingleRow = selectedTerms.length === 1;
    const isGroupAction = selectedTerms.length > 1;
    const isProvisions = typeModal === "provisions";
    const isGlobalProvisions = typeModal === "globalProvisions";

    const singleTerm = isSingleRow ? selectedTerms[0] : undefined;

    const termsCanReinstate = useMemo(
        () => (isGroupAction ? selectedTerms.filter(termCanReinstate) : []),
        [isGroupAction, selectedTerms],
    );

    // Global provisions: fetch impacted terms from backend (re-fetch when date changes)
    useEffect(() => {
        if (!isGlobalProvisions || !activePeriod) return;
        const releasedSince = date instanceof Date ? toLocalDateString(date) : undefined;
        dispatch(fetchGlobalProvisionImpactedTerms({ periodId: activePeriod, releasedSince }));
    }, [isGlobalProvisions, activePeriod, date, dispatch]);

    // Count of reinstate-eligible terms among global impacted terms
    const globalTermsCanReinstateCount = useMemo(
        () =>
            isGlobalProvisions && globalProvisionImpactedTermsStatus === "succeeded"
                ? globalProvisionImpactedTerms.filter(termCanReinstateFromStore).length
                : 0,
        [isGlobalProvisions, globalProvisionImpactedTerms, globalProvisionImpactedTermsStatus],
    );

    // Show reinstate field: single row whose term can reinstate, or group with at least one,
    // OR global provision with at least one eligible term
    const showReinstateField =
        (isProvisions &&
            ((isSingleRow && !!singleTerm && termCanReinstate(singleTerm)) ||
                (isGroupAction && termsCanReinstate.length > 0))) ||
        (isGlobalProvisions && globalTermsCanReinstateCount > 0);

    // Fetch previousInitialProvision only for single row when reinstate field is visible
    useEffect(() => {
        if (!showReinstateField || !isSingleRow || !singleTerm || !activePeriod) return;
        dispatch(fetchPreviousInitialProvision({ termId: singleTerm.id, periodId: activePeriod })).then((res) => {
            if (res.meta.requestStatus === "fulfilled") {
                setPreviousInitialProvision((res.payload as any)?.data ?? null);
            }
        });
    }, [showReinstateField, isSingleRow, singleTerm?.id, activePeriod]);

    const handleChangePercentage = (e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value.replace(/,/g, ".");
        val = val.match(/(([0-9]*([.]))?[0-9]+)|[.]/g)?.join("") ?? "";
        if (val.length > 1 && val.startsWith("0") && val[1] !== ".") val = val.substring(1);
        if (+val > 100) return;
        setPercentage(val);
    };

    const contractBadgeRules: Record<string, IStatusRule> = {
        concerned: { color: "purple", text: "" },
        selected: { color: "purple", text: "" },
    };

    // ── Live calculation hints (single row only) ───────────────────────────

    const singleTermCanReinstate = isSingleRow && !!singleTerm && termCanReinstate(singleTerm);

    const physicalProvisionHint = useMemo(() => {
        if (isGlobalProvisions || !isSingleRow || !singleTerm) return null;
        const {
            quantity = 0,
            initialProvision = 0,
            currentProvision = 0,
            provisionPercent = 0,
            reinstatedProvisionPercent = 0,
        } = singleTerm;

        if (previousInitialProvision === null) return null;

        const base = computeBasePhysicalProvision(
            currentProvision,
            previousInitialProvision,
            reinstatedProvisionPercent,
        );

        // The generated physical royalties
        const grossPhysical = quantity + base - initialProvision;

        if (!singleTermCanReinstate) {
            if (!percentage) {
                // When currentProvision < 2 we can considered it as 0
                // Somehow when it's actually 0 as we are dealing with values in cents, it becomes 1 cents during the process
                return {
                    label: t("Management.CurrentPhysicalProvision"),
                    value: toCurrency(currentProvision < 2 ? 0 : currentProvision),
                };
            }
            const newVal = computeNewPhysicalProvision(currentProvision, +percentage, provisionPercent, grossPhysical);
            return { label: t("Management.NewPhysicalProvision"), value: toCurrency(newVal) };
        }

        if (!percentage) {
            return { label: t("Management.CurrentPhysicalProvision"), value: toCurrency(base < 2 ? 0 : base) };
        }
        const newVal = computeNewPhysicalProvision(base, +percentage, provisionPercent, grossPhysical);
        return { label: t("Management.NewPhysicalProvision"), value: toCurrency(newVal) };
    }, [isSingleRow, singleTerm, percentage, previousInitialProvision, singleTermCanReinstate, t]);

    const reinstateHint = useMemo(() => {
        if (!isSingleRow || !singleTerm || previousInitialProvision === null) return null;
        const { reinstatedProvisionPercent = 0, initialProvision = 0 } = singleTerm;

        if (!reinstatePercentage) {
            return [
                {
                    label: t("Management.CurrentReinstatement"),
                    value: toCurrency(
                        computeCurrentReinstatement(previousInitialProvision, reinstatedProvisionPercent),
                    ),
                },
            ];
        }
        const newReinstatement = computeNewReinstatement(previousInitialProvision, +reinstatePercentage);
        return [
            {
                label: t("Management.NewReinstatement"),
                value: toCurrency(newReinstatement),
            },
            {
                label: t("Management.NewInitialProvision"),
                value: toCurrency(previousInitialProvision - newReinstatement),
            },
        ];
    }, [isSingleRow, singleTerm, reinstatePercentage, previousInitialProvision, t]);

    // ── Submit ─────────────────────────────────────────────────────────────

    const onSubmit = () => {
        if (!activePeriod) return;

        if (isGlobalProvisions) {
            const releasedSince = date instanceof Date ? toLocalDateString(date) : undefined;
            dispatch(
                addGlobalProvision({
                    periodId: activePeriod,
                    payload: {
                        releasedSince,
                        ...(percentage ? { provisionPercent: +percentage * 100 } : {}),
                        ...(showReinstateField && reinstatePercentage
                            ? { reinstatePercent: +reinstatePercentage * 100 }
                            : {}),
                    },
                }),
            ).then((res) => {
                if (res.meta.requestStatus === "fulfilled") close();
            });
            return;
        }

        if (isProvisions) {
            dispatch(
                addProvisions({
                    termId: percentage ? selectedTermIds : undefined,
                    termIdReinstate:
                        reinstatePercentage && showReinstateField
                            ? isSingleRow
                                ? selectedTermIds
                                : termsCanReinstate.map((t) => t.id)
                            : undefined,
                    percent: percentage ? +percentage * 100 : undefined,
                    periodId: activePeriod,
                    reinstatePercent:
                        reinstatePercentage && showReinstateField ? +reinstatePercentage * 100 : undefined,
                }),
            ).then((res) => {
                if (res.meta.requestStatus === "fulfilled") close();
            });
        }
    };

    // ── Loading state ──────────────────────────────────────────────────────

    const showLoadingStatus = [
        addProvisionsStatus,
        addGlobalProvisionStatus,
        ...(isGlobalProvisions ? [globalProvisionImpactedTermsStatus] : []),
    ].includes("pending");

    if (showLoadingStatus) {
        return (
            <Box sx={{ width: "100%", textAlign: "center" }}>
                <div>{t("Management.LoadingAddProvisions")}</div>
                <CircularProgress sx={{ color: variables["primary-purple"] }} />
            </Box>
        );
    }

    // ── Validation ─────────────────────────────────────────────────────────

    const isInvalidPercentage = (+percentage > 0 && isNaN(+percentage)) || +percentage > 100;
    const isInvalidReinstatePercentage =
        +reinstatePercentage > 0 && (isNaN(+reinstatePercentage) || +reinstatePercentage > 100);
    const absentPercentages = !percentage && !reinstatePercentage;
    const isSubmitDisabled = isGlobalProvisions
        ? date == "Invalid Date" ||
          (!percentage && !reinstatePercentage) ||
          isInvalidPercentage ||
          isInvalidReinstatePercentage
        : isProvisions && (absentPercentages || isInvalidPercentage || isInvalidReinstatePercentage);

    // ── Render ─────────────────────────────────────────────────────────────

    return (
        <div className="add-provision">
            {/* Header */}
            <div className="add-provision-info">
                {isGlobalProvisions ? (
                    <span className="add-provision-info__text">{t("Management.AllTermsSelected")}</span>
                ) : (
                    <StatusChip
                        status="selected"
                        statusRules={contractBadgeRules}
                        label={`${selectedRows.length} ${t(
                            selectedRows.length > 1 ? "Management.ContractsSelected" : "Management.ContractSelected",
                        )}`}
                    />
                )}
                {isGlobalProvisions && (
                    <MessageBlock
                        type="warning"
                        text={t("Management.ProvisionOverwrite")}
                        small
                        style={{ marginTop: "10px" }}
                    />
                )}
            </div>

            {/* Date picker — globalProvisions only */}
            {isGlobalProvisions && (
                <div className="add-provision-input">
                    <div className="add-provision-input-label">
                        <span className="name">{t("Management.From")}</span>
                        <span className="optional-mark">
                            <i>{t("Management.Optional")}</i>
                        </span>
                    </div>
                    <StyledDatePicker
                        value={date}
                        inputFormat="dd/MM/yyyy"
                        onChange={(v: Date | null) => setDate(v)}
                        renderInput={(params: any) => <StyledTextField fullWidth {...params} />}
                    />
                </div>
            )}

            {/* Field 1: provision percentage on physical sales */}
            <div className="add-provision-input">
                <div className="add-provision-input-label">
                    <span>{t("Management.PercentageOfGross")}</span>
                </div>
                <StyledTextField
                    fullWidth
                    value={percentage}
                    onChange={handleChangePercentage}
                    placeholder={"0"}
                    InputProps={{
                        endAdornment: (
                            <span style={{ color: "#585858", marginLeft: "10px", fontWeight: "600" }}>%</span>
                        ),
                    }}
                />
                {isSingleRow && physicalProvisionHint && (
                    <SmallBody color="grey">
                        {physicalProvisionHint.label} {physicalProvisionHint.value}
                    </SmallBody>
                )}
            </div>

            {/* Field 2: reinstatement — toggle + collapsible field */}
            {showReinstateField && (
                <ReinstateProvisionField
                    affectedCount={
                        isGlobalProvisions ? globalTermsCanReinstateCount : isSingleRow ? 1 : termsCanReinstate.length
                    }
                    value={reinstatePercentage}
                    onChange={setReinstatePercentage}
                    hints={isSingleRow && !isGlobalProvisions ? reinstateHint ?? undefined : undefined}
                    hideGroupInfo={isGlobalProvisions}
                    defaultEnabled={
                        !!reinstatePercentage ||
                        selectedTerms.some((term) => (term.reinstatedProvisionPercent ?? 0) > 0)
                    }
                />
            )}

            <StyledButton buttonType="primary" disabled={isSubmitDisabled} fullWidth onClick={onSubmit}>
                {t("Management.AddProvisions")}
            </StyledButton>
        </div>
    );
};

export default AddProvisions;
